// ============================================================
// Fee Types Page
// ============================================================
// Submodule under Fee module to manage master fee types.

import { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Button, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, FormControl, InputLabel, Select, FormControlLabel, Switch, InputAdornment, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, Edit2, Trash2, Search, Tag, CheckCircle2, Percent, Layers } from 'lucide-react';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { StatCard } from '@/components/cards/StatCard';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import { useFeeTypes, useCreateFeeType, useUpdateFeeType, useDeleteFeeType } from '@/hooks/useFeeTypes';
import toast from 'react-hot-toast';
import { enumApi } from '@/api/enum.api';

const DEFAULT_FORM_DATA = {
  name: '',
  code: '',
  description: '',
  category: 'ACADEMIC',
  frequency: 'MONTHLY',
  taxable: false,
  tax_percentage: 0,
  refundable: false,
  is_optional: false,
  is_active: true,
};

export function FeeTypesPage() {
  usePageMeta('Fee Types', [
    { label: 'Fees', path: '/fees' },
    { label: 'Fee Types' },
  ]);

  const { data: feeTypes = [], isLoading } = useFeeTypes();
  const { mutate: createFeeType, isPending: isCreating } = useCreateFeeType();
  const { mutate: updateFeeType, isPending: isUpdating } = useUpdateFeeType();
  const { mutate: deleteFeeType, isPending: isDeleting } = useDeleteFeeType();

  const [categories, setCategories] = useState([]);
  const [frequencies, setFrequencies] = useState([]);

  useEffect(() => {
    const fetchEnums = async () => {
      try {
        const [cats, freqs] = await Promise.all([
          enumApi.getFeeCategories(),
          enumApi.getFeeFrequencies()
        ]);
        setCategories(cats || []);
        setFrequencies(freqs || []);
      } catch (err) {
        console.error('Failed to fetch enums', err);
      }
    };
    fetchEnums();
  }, []);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedFrequency, setSelectedFrequency] = useState('ALL');
  const [selectedStatus, setSelectedStatus] = useState('ALL');

  // Modal State
  const [openModal, setOpenModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  // Delete Dialog State
  const [deleteId, setDeleteId] = useState(null);

  // Stats Calculations
  const stats = useMemo(() => {
    const total = feeTypes.length;
    const active = feeTypes.filter((f) => f.is_active).length;
    const academic = feeTypes.filter((f) => f.category === 'ACADEMIC').length;
    const taxable = feeTypes.filter((f) => f.taxable).length;

    return { total, active, academic, taxable };
  }, [feeTypes]);

  // Filtered List
  const filteredFeeTypes = useMemo(() => {
    return feeTypes.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory =
        selectedCategory === 'ALL' || item.category === selectedCategory;

      const matchesFrequency =
        selectedFrequency === 'ALL' || item.frequency === selectedFrequency;

      const matchesStatus =
        selectedStatus === 'ALL' ||
        (selectedStatus === 'ACTIVE' && item.is_active) ||
        (selectedStatus === 'INACTIVE' && !item.is_active);

      return matchesSearch && matchesCategory && matchesFrequency && matchesStatus;
    });
  }, [feeTypes, searchTerm, selectedCategory, selectedFrequency, selectedStatus]);

  // Handlers
  const handleOpenAddModal = () => {
    setEditingItem(null);
    setFormData(DEFAULT_FORM_DATA);
    setOpenModal(true);
  };

  const handleOpenEditModal = (row) => {
    let freq = row.frequency || 'MONTHLY';
    if (freq === 'ANNUALLY') freq = 'YEARLY';
    if (freq === 'PER_TERM') freq = 'CUSTOM';
    setEditingItem(row);
    setFormData({
      name: row.name || '',
      code: row.code || '',
      description: row.description || '',
      category: row.category || 'ACADEMIC',
      frequency: freq,
      taxable: !!row.taxable,
      tax_percentage: row.tax_percentage || 0,
      refundable: !!row.refundable,
      is_optional: !!row.is_optional,
      is_active: row.is_active ?? true,
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingItem(null);
    setFormData(DEFAULT_FORM_DATA);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error('Name and Code are required.');
      return;
    }

    let freq = formData.frequency || 'MONTHLY';
    if (freq === 'ANNUALLY') freq = 'YEARLY';
    if (freq === 'PER_TERM') freq = 'CUSTOM';

    const payload = {
      ...formData,
      frequency: freq,
      tax_percentage: formData.taxable ? Number(formData.tax_percentage) : 0,
    };

    if (editingItem) {
      updateFeeType(
        { id: editingItem.id, payload },
        {
          onSuccess: () => {
            toast.success('Fee type updated successfully!');
            handleCloseModal();
          },
          onError: (err) => {
            const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to update fee type.';
            toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to update fee type.');
          },
        }
      );
    } else {
      createFeeType(payload, {
        onSuccess: () => {
          toast.success('Fee type created successfully!');
          handleCloseModal();
        },
        onError: (err) => {
          const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to create fee type.';
          toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to create fee type.');
        },
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    deleteFeeType(deleteId, {
      onSuccess: () => {
        toast.success('Fee type deleted successfully!');
        setDeleteId(null);
      },
      onError: (err) => {
        const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to delete fee type.';
        toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to delete fee type.');
      },
    });
  };

  const columns = [
    {
      field: 'code',
      headerName: 'Code',
      flex: 1.1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.5px',
              borderColor: tokens.colors.primary.main,
              color: tokens.colors.primary.main,
              backgroundColor: `${tokens.colors.primary.main}0D`,
              maxWidth: 'none',
            }}
          />
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Fee Type Name',
      flex: 1.5,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', py: 0.5, pr: 2, overflow: 'hidden' }}>
          <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
            {params.value}
          </Typography>
          {params.row.description && (
            <Tooltip title={params.row.description} placement="top-start" arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  cursor: 'pointer',
                }}
              >
                {params.row.description}
              </Typography>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => {
        const catColorMap = {
          ACADEMIC: 'primary',
          TRANSPORT: 'warning',
          HOSTEL: 'secondary',
          EXAM: 'error',
          LIBRARY: 'info',
          SPORTS: 'success',
          OTHER: 'default',
        };
        const dynamicCat = categories.find(c => c.value === params.value);
        const label = dynamicCat ? dynamicCat.label : params.value;
        const color = catColorMap[params.value] || 'default';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip label={label} size="small" color={color} />
          </Box>
        );
      },
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      flex: 0.8,
      minWidth: 95,
      renderCell: (params) => {
        const freqObj = frequencies.find((f) => f.value === params.value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {freqObj ? freqObj.label : params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'taxable',
      headerName: 'Taxable',
      flex: 0.7,
      minWidth: 85,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value ? (
            <Chip
              label={`${params.row.tax_percentage || 0}% Tax`}
              size="small"
              color="warning"
              variant="outlined"
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              No Tax
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'refundable',
      headerName: 'Refundable',
      flex: 0.6,
      minWidth: 80,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value ? 'Yes' : 'No'}
            size="small"
            color={params.value ? 'info' : 'default'}
            variant="text"
          />
        </Box>
      ),
    },
    {
      field: 'is_optional',
      headerName: 'Optional',
      flex: 0.8,
      minWidth: 95,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value ? 'Optional' : 'Mandatory'}
            size="small"
            color={params.value ? 'warning' : 'success'}
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created On',
      flex: 0.8,
      minWidth: 95,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="caption" color="text.secondary">
            {params.value ? params.value.split('T')[0] : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 85,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            size="small"
            color={params.value ? 'success' : 'error'}
            sx={{ fontWeight: 500 }}
          />
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 85,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Tooltip title="Edit Fee Type">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenEditModal(params.row)}
            >
              <Edit2 size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Fee Type">
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteId(params.row.id)}
            >
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader
        title="Fee Types"
        subtitle="Manage master categories, codes, tax settings, and frequencies for school fees."
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={handleOpenAddModal}
          >
            Add Fee Type
          </Button>
        }
      />

      {/* Stats Summary Cards */}
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 4 }} sx={{ mt: 1 }}>
        <StatCard
          title="Total Fee Types"
          value={stats.total}
          icon={Tag}
          color="primary"
        />
        <StatCard
          title="Active Fee Types"
          value={stats.active}
          icon={CheckCircle2}
          color="success"
        />
        <StatCard
          title="Academic Fees"
          value={stats.academic}
          icon={Layers}
          color="info"
        />
        <StatCard
          title="Taxable Types"
          value={stats.taxable}
          icon={Percent}
          color="warning"
        />
      </DashboardGrid>

      {/* Filters & DataGrid inside WidgetContainer matching other fee pages */}
      <WidgetContainer noPadding sx={{ mt: 3 }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.colors.border}`, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search by name, code or description..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={tokens.colors.text.secondary} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="ALL"><em>All Categories</em></MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Frequency</InputLabel>
            <Select
              value={selectedFrequency}
              label="Frequency"
              onChange={(e) => setSelectedFrequency(e.target.value)}
            >
              <MenuItem value="ALL"><em>All Frequencies</em></MenuItem>
              {frequencies.map((freq) => (
                <MenuItem key={freq.value} value={freq.value}>{freq.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="ALL"><em>All Status</em></MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={filteredFeeTypes}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Box>
      </WidgetContainer>

      {/* Create / Edit Fee Type Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ fontWeight: 600 }}>
            {editingItem ? 'Edit Fee Type' : 'Create Fee Type'}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <TextField
                  label="Fee Name"
                  fullWidth
                  required
                  size="small"
                  placeholder="e.g. Monthly Tuition Fee"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="Fee Code"
                  fullWidth
                  required
                  size="small"
                  placeholder="e.g. TUI001"
                  value={formData.code}
                  onChange={(e) => handleFormChange('code', e.target.value.toUpperCase())}
                  sx={{ flex: 1 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <FormControl size="small" fullWidth required sx={{ flex: 1 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category"
                    onChange={(e) => handleFormChange('category', e.target.value)}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth required sx={{ flex: 1 }}>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={formData.frequency}
                    label="Frequency"
                    onChange={(e) => handleFormChange('frequency', e.target.value)}
                  >
                    {frequencies.map((freq) => (
                      <MenuItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                size="small"
                placeholder="Description (Optional)"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />

              <Box sx={{ p: 2, border: `1px solid ${tokens.colors.border}`, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  Tax & Fee Options
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.taxable}
                        onChange={(e) => handleFormChange('taxable', e.target.checked)}
                      />
                    }
                    label="Taxable Fee"
                  />
                  {formData.taxable && (
                    <TextField
                      label="Tax Percentage (%)"
                      type="number"
                      size="small"
                      sx={{ width: 160 }}
                      slotProps={{ htmlInput: { min: 0, max: 100, step: 0.1 } }}
                      value={formData.tax_percentage}
                      onChange={(e) => handleFormChange('tax_percentage', e.target.value)}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.refundable}
                        onChange={(e) => handleFormChange('refundable', e.target.checked)}
                      />
                    }
                    label="Refundable"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_optional}
                        onChange={(e) => handleFormChange('is_optional', e.target.checked)}
                      />
                    }
                    label="Optional for Students"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => handleFormChange('is_active', e.target.checked)}
                        color="success"
                      />
                    }
                    label="Is Active"
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isCreating || isUpdating}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCreating || isUpdating}
            >
              {isCreating || isUpdating
                ? 'Saving...'
                : editingItem
                  ? 'Save Changes'
                  : 'Create Fee Type'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        maxWidth="xs"
        fullWidth
        disableRestoreFocus
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this fee type? This action cannot be undone and may affect active fee structures using this type.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            sx={{ borderRadius: 2 }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Fee Type'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
